import { CreativeWaterVisibility } from './CreativeWaterVisibility';
import { CreativeEnvironment } from './CreativeEnvironment';
import { CREATIVE_VILLAGE_ID } from './CreativeMode';
import { CreativeSkybox } from './CreativeSkybox';
import {
  CREATIVE_BIOME_TEXTURES,
  configureCreativeTerrainMaterial,
} from './CreativeTerrainMaterial';
import * as THREE from 'three';
import type {
  CreativeSpatialPayload,
  AvatarAppearanceLayer,
  AvatarEmoteAction,
  AvatarJugglingPayload,
  EntityChunkPayload,
  TerrainPayload,
  TerrainChunkPayload,
  VillageCharactersPayload,
} from '../gateway/contracts';
import { cameraDroneOrbitAngle, cameraDroneTransitionProgress } from './CameraDroneMotion';
import type { LegacyAvatarScene } from './LegacyAvatarScene';
import { LegacyBuildingReadyEffects } from './LegacyBuildingReadyEffects';
import { legacyObjectLevitationAt } from './LegacyObjectLevitation';
import type { LegacySkybox } from './LegacySkybox';
import { LegacyShadowSampling } from './LegacyShadowSampling';
import type { LegacyWaterSurface } from './LegacyWaterSurface';
import type { LegacyWorldAssets, RenderedEntityChunk } from './LegacyWorldAssets';
import { updateLegacyWorldShadowProjection } from './LegacyWorldLighting';
import type { TerrainHeightField } from './TerrainHeightField';
import type { TerrainChunkMeshFactory } from './TerrainChunkMeshFactory';
import type { BrowserAvatarPresence } from './VillagePresence';

type CameraStatus = Readonly<{
  generation: number;
  source: string | null;
  liveSince: number;
  switchAt: number;
  nextEvaluationAt: number;
  serverTime: number;
}>;
type CameraConfiguration = Readonly<{
  mode: 'fixed' | 'orbit';
  centerTileX: number;
  centerTileZ: number;
  radius: number;
  height: number;
  markerTileX: number;
  markerTileZ: number;
  rotation: number;
  revolutionSeconds: number;
  fixedHoverHeight: number;
}>;
type PublicActor = Readonly<{
  actorId: string;
  appearanceLayers: readonly AvatarAppearanceLayer[];
  posX: number;
  posY?: number;
  posZ: number;
  rotation: number;
  away: boolean;
  gender: 'man' | 'woman';
  emote: string | null;
  gesture: string | null;
  sit: boolean;
  juggling: PublicJuggling | boolean | null;
  petType: number;
  petNetworkId: number;
  mountType: number;
  noboType?: number;
}>;
type PublicJuggling = Readonly<{
  sequence: number;
  siteswap: string;
  prop: 'b' | 'c' | 'r';
  elapsedMilliseconds: number;
}>;
type PublicCharacter = Readonly<{
  actorId: string;
  posX: number;
  posZ: number;
  rotation?: number;
  type: number;
  petType: number;
  petNetworkId: number;
  mountType: number;
  noboType: number;
  inBuilding: boolean;
}>;
type PublicGateState = Readonly<{
  buildingId: number;
  open: boolean;
}>;
type PublicProductionState = Readonly<{
  buildingId: number;
  ready: boolean;
  readyAt: number;
}>;
type SourceSnapshot = Readonly<{
  sourceId: string;
  camera: CameraConfiguration;
  terrain: Readonly<{
    theme: number;
    villageSize: number;
    revision: number;
    sampleWidth: number;
    heightEncoding: 'signed-int8-base64';
    heightSamples: string;
    streamed?: boolean;
  }>;
  entityRevision: number;
  actors: readonly PublicActor[];
  characters?: readonly PublicCharacter[];
  gateStates?: readonly PublicGateState[];
  productionStates?: readonly PublicProductionState[];
}>;
type EntityChunk = Readonly<{
  terrainChunk?: TerrainChunkPayload;
  creativeSpatial?: CreativeSpatialPayload;
  revision: number;
  chunkX: number;
  chunkZ: number;
  buildings: readonly Readonly<{
    placementY?: number | null;
    placementOffsetX?: number;
    placementOffsetZ?: number;
    placementTiltX?: number;
    placementTiltZ?: number;
    id: number;
    type: number;
    tileX: number;
    tileZ: number;
    rotation: number;
    ready: boolean;
    open: boolean;
    pictureId: number | null;
  }>[];
  trees: readonly Readonly<{
    id: number;
    batch: number;
    type: number;
    tileX: number;
    tileZ: number;
    scaleByte: number;
    scale: number;
  }>[];
}>;

const FRAME_MILLISECONDS = 1000 / 20;
const PUBLIC_CAMERA_SHADOW_MAP_SIZE = 512;
const PUBLIC_CAMERA_INACTIVITY_MILLISECONDS = 5 * 60 * 1000;
const PUBLIC_CAMERA_VIEWER_HEADER = 'X-Public-Camera-Viewer';
const TILE_WORLD_SCALE = 0.5;

/** A session-free, read-only renderer used only behind the anonymous forms. */
export class PublicCameraScene {
  private renderer: THREE.WebGLRenderer | undefined;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(48, 1, 0.1, 300);
  private readonly world = new THREE.Group();
  private readonly shadowSampling = new LegacyShadowSampling('low');
  private readonly publicIds = new Map<string, number>();
  private readonly entityGroups = new Map<string, THREE.Group>();
  private readonly readyEffects = new LegacyBuildingReadyEffects(this.scene, () =>
    this.entityGroups.values(),
  );
  private readonly entityPayloads = new Map<string, EntityChunkPayload>();
  private readonly objectLevitationByGridCell = new Map<number, number>();
  private readonly terrainChunks = new Map<string, THREE.Mesh>();
  private readonly gateStates = new Map<number, boolean>();
  private readonly productionStates = new Map<number, PublicProductionState>();
  private avatarScene: LegacyAvatarScene | undefined;
  private nextPublicId = 1;
  private terrain: TerrainHeightField | undefined;
  private terrainFactory: TerrainChunkMeshFactory | undefined;
  private terrainTexture: THREE.Texture | undefined;
  private waterSurface: LegacyWaterSurface | undefined;
  private water: THREE.Mesh | undefined;
  private readonly waterVisibility = new CreativeWaterVisibility();
  private waterTexture: THREE.Texture | undefined;
  private skybox: LegacySkybox | undefined;
  private readonly creativeEnvironment = new CreativeEnvironment();
  private readonly creativeTerrainAmbient = new THREE.Color();
  private readonly underwaterBackground = new THREE.Color();
  private ambient: THREE.AmbientLight | undefined;
  private worldAssets: LegacyWorldAssets | undefined;
  private sun: THREE.DirectionalLight | undefined;
  private status: CameraStatus | undefined;
  private source: SourceSnapshot | undefined;
  private abort = new AbortController();
  private frame = 0;
  private statusRetry: ReturnType<typeof setTimeout> | undefined;
  private productionReadyTimeout: ReturnType<typeof setTimeout> | undefined;
  private inactivityTimeout: ReturnType<typeof setTimeout> | undefined;
  private lastFrame = 0;
  private lastCanvasWidth = 0;
  private lastCanvasHeight = 0;
  private serverOffset = 0;
  private disposed = false;
  private inactive = false;
  private polling = false;
  private viewerToken: string | undefined;
  private entityRefreshQueue: Promise<void> = Promise.resolve();
  private readonly reducedMotion =
    typeof globalThis.matchMedia === 'function' &&
    globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
  private readonly visibilityChanged = (): void => {
    if (document.hidden) {
      this.abort.abort();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.statusRetry);
      this.releaseViewer();
      return;
    }
    if (this.inactive) return;
    this.resetInactivityTimeout();
    this.abort = new AbortController();
    void this.refresh(true).catch(() => this.fallback());
  };
  private readonly userActivity = (): void => this.resetInactivityTimeout();

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.scene.add(this.world);
    this.scene.background = new THREE.Color(0x91c6df);
    this.scene.fog = new THREE.Fog(0x91c6df, 45, 125);
    document.addEventListener('visibilitychange', this.visibilityChanged);
    document.addEventListener('pointerdown', this.userActivity);
    document.addEventListener('keydown', this.userActivity);
    document.addEventListener('input', this.userActivity);
    this.resetInactivityTimeout();
  }

  async start(): Promise<void> {
    try {
      this.renderer = new THREE.WebGLRenderer({
        canvas: this.canvas,
        antialias: true,
        alpha: true,
      });
      this.renderer.setPixelRatio(Math.min(globalThis.devicePixelRatio || 1, 1.25));
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFShadowMap;
      await this.refresh(true);
    } catch {
      this.fallback();
    }
  }

  setRegistrationOpen(open: boolean): void {
    this.canvas.classList.toggle('public-camera--registration-hidden', open);
  }

  e2eNoboPresentations(): readonly Readonly<{
    noboType: number;
    worldX: number;
    worldZ: number;
  }>[] {
    return this.avatarScene?.standaloneNoboPresentations() ?? [];
  }

  dispose(): void {
    this.disposed = true;
    this.abort.abort();
    cancelAnimationFrame(this.frame);
    clearTimeout(this.statusRetry);
    clearTimeout(this.inactivityTimeout);
    document.removeEventListener('visibilitychange', this.visibilityChanged);
    document.removeEventListener('pointerdown', this.userActivity);
    document.removeEventListener('keydown', this.userActivity);
    document.removeEventListener('input', this.userActivity);
    this.releaseViewer();
    this.disposeWorld();
    this.readyEffects.dispose();
    this.worldAssets?.dispose();
    this.renderer?.dispose();
    this.renderer?.forceContextLoss();
  }

  private async refresh(full: boolean): Promise<void> {
    if (this.disposed || this.inactive || document.hidden) return;
    const response = await fetch('/api/v2/public-camera/status?viewer=1', {
      headers: this.viewerHeaders(),
      signal: this.abort.signal,
    });
    if (!response.ok) throw new Error('Public camera status unavailable');
    const status = (await response.json()) as {
      camera: CameraStatus;
      viewer?: Readonly<{ token: string; expiresAt: number }>;
    };
    if (status.viewer !== undefined) this.viewerToken = status.viewer.token;
    this.serverOffset = status.camera.serverTime - Date.now();
    const changed = status.camera.source !== this.status?.source;
    this.status = status.camera;
    if (status.camera.source === null) {
      this.viewerToken = undefined;
      this.canvas.classList.remove('public-camera--ready');
      this.source = undefined;
      this.disposeWorld();
      clearTimeout(this.statusRetry);
      this.statusRetry = setTimeout(() => {
        void this.refresh(false).catch(() => this.fallback());
      }, 10_000);
      return;
    }
    if (this.viewerToken === undefined) throw new Error('Public camera viewer unavailable');
    clearTimeout(this.statusRetry);
    if (changed || full || this.source === undefined) {
      this.canvas.classList.remove('public-camera--ready');
      await this.loadSource(status.camera.source);
      requestAnimationFrame(() => this.canvas.classList.add('public-camera--ready'));
    }
    this.resize();
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(this.renderFrame);
    if (!this.polling) void this.poll();
  }

  private async loadSource(sourceId: string): Promise<void> {
    const response = await fetch(`/api/v2/public-camera/sources/${encodeURIComponent(sourceId)}`, {
      headers: this.viewerHeaders(),
      signal: this.abort.signal,
    });
    if (!response.ok) throw new Error('Public camera source unavailable');
    const payload = (await response.json()) as { source: SourceSnapshot };
    this.source = payload.source;
    this.disposeWorld();
    await this.loadWorld(payload.source);
    this.replaceActors(payload.source.actors);
    this.replaceCharacters(payload.source.characters ?? []);
    this.replaceGateStates(payload.source.gateStates ?? []);
    this.replaceProductionStates(payload.source.productionStates ?? [], false);
  }

  private async loadWorld(source: SourceSnapshot): Promise<void> {
    const [
      terrainModule,
      chunkModule,
      assetsModule,
      themeModule,
      materialModule,
      skyboxModule,
      lightsModule,
      waterModule,
      avatarModule,
    ] = await Promise.all([
      import('./TerrainHeightField'),
      import('./TerrainChunkMeshFactory'),
      import('./LegacyWorldAssets'),
      import('./LegacyThemeCatalog'),
      import('./LegacyTerrainMaterial'),
      import('./LegacySkybox'),
      import('./LegacyWorldLighting'),
      import('./LegacyWaterSurface'),
      import('./LegacyAvatarScene'),
    ]);
    this.abort.signal.throwIfAborted();
    const terrainPayload: TerrainPayload = {
      villageId: source.terrain.sampleWidth > 129 ? CREATIVE_VILLAGE_ID : 0,
      theme: source.terrain.theme,
      villageSize: source.terrain.villageSize,
      revision: source.terrain.revision,
      sampleWidth: source.terrain.sampleWidth,
      heightEncoding: source.terrain.heightEncoding,
      heightSamples: source.terrain.heightSamples,
      ...(source.terrain.streamed ? { streamed: true } : {}),
    };
    const terrain = terrainModule.TerrainHeightField.fromGateway(terrainPayload);
    this.terrain = terrain;
    this.avatarScene = new avatarModule.LegacyAvatarScene(
      this.scene,
      terrain,
      this.camera,
      (worldX, worldZ) => this.objectLevitationAt(worldX, worldZ),
    );
    // Public viewers never own an avatar. Setting an unused local id only enables
    // the renderer's smooth remote interpolation path.
    this.avatarScene.setLocalPlayer(0);
    this.avatarScene.setSpeechBubblesHidden(true);
    const environment = themeModule.legacyThemeEnvironment(terrain.theme);
    this.skybox =
      terrain.villageId === CREATIVE_VILLAGE_ID
        ? new CreativeSkybox()
        : new skyboxModule.LegacySkybox(environment);
    this.scene.background = this.skybox.texture;
    this.scene.fog = new THREE.Fog(
      new THREE.Color(...environment.fogColor),
      environment.fogNear,
      environment.fogFar,
    );
    const lights = lightsModule.createLegacyWorldLights(environment.ambientColor);
    this.ambient = lights.ambient;
    this.sun = lights.source;
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(PUBLIC_CAMERA_SHADOW_MAP_SIZE, PUBLIC_CAMERA_SHADOW_MAP_SIZE);
    this.world.add(lights.ambient, lights.source);

    const material = new THREE.MeshLambertMaterial({
      color: new THREE.Color(0x75ad5a).multiplyScalar(
        materialModule.LEGACY_TERRAIN_DIFFUSE_COEFFICIENT,
      ),
    });
    materialModule.configureLegacyTerrainMaterial(
      material,
      environment.shoreline,
      environment.shorelineColor,
      environment.ambientColor,
      this.shadowSampling,
      terrain.villageId === CREATIVE_VILLAGE_ID
        ? this.creativeTerrainAmbient.setRGB(...environment.ambientColor)
        : undefined,
    );
    const texture = await new THREE.TextureLoader().loadAsync(
      `/legacy/landscape/${environment.terrainTexture}`,
    );
    this.abort.signal.throwIfAborted();
    texture.colorSpace = THREE.NoColorSpace;
    texture.wrapS = texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.magFilter = THREE.LinearFilter;
    material.map = texture;
    if (terrain.villageId === CREATIVE_VILLAGE_ID) {
      const textures = await Promise.all(
        CREATIVE_BIOME_TEXTURES.map((name) =>
          new THREE.TextureLoader().loadAsync(`/legacy/landscape/${name}`),
        ),
      );
      for (const value of textures) {
        value.colorSpace = THREE.NoColorSpace;
        value.wrapS = value.wrapT = THREE.ClampToEdgeWrapping;
      }
      configureCreativeTerrainMaterial(material, textures);
      material.addEventListener('dispose', () => textures.forEach((value) => value.dispose()));
    }
    material.color.setScalar(materialModule.LEGACY_TERRAIN_DIFFUSE_COEFFICIENT);
    material.needsUpdate = true;
    this.terrainTexture = texture;

    const waterSurface = new waterModule.LegacyWaterSurface(terrain.width - 1);
    const waterTexture = await new THREE.TextureLoader().loadAsync(
      `/legacy/landscape/${environment.waterTexture}`,
    );
    this.abort.signal.throwIfAborted();
    waterTexture.colorSpace = THREE.SRGBColorSpace;
    waterTexture.wrapS = waterTexture.wrapT = THREE.RepeatWrapping;
    waterTexture.magFilter = THREE.LinearFilter;
    const water = new THREE.Mesh(
      waterSurface.geometry,
      new THREE.MeshPhongMaterial({
        map: waterTexture,
        color: 0xffffff,
        shininess: 30,
        side: terrain.villageId === CREATIVE_VILLAGE_ID ? THREE.DoubleSide : THREE.FrontSide,
      }),
    );
    water.frustumCulled = false;
    water.visible = environment.waterVisible;
    water.name = 'public-camera-water';
    this.waterSurface = waterSurface;
    this.water = water;
    this.waterTexture = waterTexture;
    this.world.add(water);

    const coordinates = this.legacyVisibleChunkCoordinates(source);
    this.worldAssets ??= new assetsModule.LegacyWorldAssets(
      (pictureId) => this.loadPublicPicture(pictureId),
      this.shadowSampling,
    );
    const renderedChunks = (
      await Promise.all(
        coordinates.map((coordinate) =>
          this.renderChunk(source.sourceId, coordinate.x, coordinate.z, terrain),
        ),
      )
    ).filter((rendered): rendered is RenderedEntityChunk => rendered !== undefined);
    this.replaceEntityGroups(renderedChunks);
    terrain.replaceBuildingGroundOverlays(
      renderedChunks.flatMap((rendered) => rendered.buildingGroundOverlays),
    );
    const planner = new (await import('./SpatialChunkPlanner')).SpatialChunkPlanner(terrain.width);
    const factory = new chunkModule.TerrainChunkMeshFactory(terrain, planner.options, material);
    this.terrainFactory = factory;
    factory.replaceMaterialOverlays(
      renderedChunks.flatMap((rendered) => rendered.terrainMaterialOverlays),
    );
    for (const coordinate of coordinates) {
      const mesh = factory.create(coordinate);
      this.terrainChunks.set(chunkKey(coordinate.x, coordinate.z), mesh);
      this.world.add(mesh);
    }
  }

  private async renderChunk(
    sourceId: string,
    chunkX: number,
    chunkZ: number,
    terrain: TerrainHeightField,
  ): Promise<RenderedEntityChunk | undefined> {
    const response = await fetch(
      `/api/v2/public-camera/sources/${encodeURIComponent(sourceId)}/entity-chunks/${String(chunkX)}/${String(chunkZ)}`,
      { headers: this.viewerHeaders(), signal: this.abort.signal },
    );
    if (!response.ok) return undefined;
    const payload = (await response.json()) as { entityChunk: EntityChunk };
    const entityPayload = publicEntityChunk(payload.entityChunk, terrain.width);
    if (entityPayload.terrainChunk !== undefined)
      terrain.installChunk(chunkKey(chunkX, chunkZ), entityPayload.terrainChunk);
    const rendered = await this.worldAssets?.createChunk(
      entityPayload,
      terrain,
      undefined,
      undefined,
      undefined,
      this.abort.signal,
    );
    if (rendered === undefined) return undefined;
    if (this.abort.signal.aborted || this.source?.sourceId !== sourceId) {
      this.worldAssets?.release(rendered.group);
      return undefined;
    }
    rendered.group.userData.nordPublicCameraEntityPayload = entityPayload;
    return rendered;
  }

  private async loadPublicPicture(pictureId: number): Promise<Blob> {
    const sourceId = this.source?.sourceId;
    if (sourceId === undefined) throw new Error('Public camera source unavailable');
    const response = await fetch(
      `/api/v2/public-camera/sources/${encodeURIComponent(sourceId)}/pictures/${String(pictureId)}`,
      { headers: this.viewerHeaders(), signal: this.abort.signal },
    );
    if (!response.ok) throw new Error('Public camera picture unavailable');
    return response.blob();
  }

  private replaceEntityGroups(renderedChunks: readonly RenderedEntityChunk[]): void {
    for (const group of this.entityGroups.values()) {
      this.world.remove(group);
      this.worldAssets?.release(group);
    }
    this.entityGroups.clear();
    this.entityPayloads.clear();
    this.objectLevitationByGridCell.clear();
    for (const rendered of renderedChunks) {
      rendered.group.traverse((candidate) => {
        if (candidate instanceof THREE.Mesh) candidate.castShadow = true;
      });
      this.entityGroups.set(rendered.group.name, rendered.group);
      const payload = rendered.group.userData.nordPublicCameraEntityPayload as
        EntityChunkPayload | undefined;
      if (payload !== undefined) this.entityPayloads.set(rendered.group.name, payload);
      this.world.add(rendered.group);
    }
  }

  private async refreshEntities(entityRevision: number): Promise<void> {
    if (this.disposed || this.inactive || document.hidden) return;
    const source = this.source;
    const terrain = this.terrain;
    if (source === undefined || terrain === undefined) return;
    const renderedChunks = (
      await Promise.all(
        this.legacyVisibleChunkCoordinates(source).map((coordinate) =>
          this.renderChunk(source.sourceId, coordinate.x, coordinate.z, terrain),
        ),
      )
    ).filter((rendered): rendered is RenderedEntityChunk => rendered !== undefined);
    if (
      this.abort.signal.aborted ||
      this.source?.sourceId !== source.sourceId ||
      entityRevision < this.source.entityRevision
    ) {
      for (const rendered of renderedChunks) this.worldAssets?.release(rendered.group);
      return;
    }
    this.replaceEntityGroups(renderedChunks);
    this.restoreOpenGateStates();
    const buildingGroundsChanged = terrain.replaceBuildingGroundOverlays(
      renderedChunks.flatMap((rendered) => rendered.buildingGroundOverlays),
    );
    const terrainMaterialsChanged =
      this.terrainFactory?.replaceMaterialOverlays(
        renderedChunks.flatMap((rendered) => rendered.terrainMaterialOverlays),
      ) ?? false;
    if (buildingGroundsChanged) this.terrainFactory?.refreshHeightField();
    if (terrain.streamed || buildingGroundsChanged || terrainMaterialsChanged)
      this.rebuildTerrainChunks();
    this.source = { ...source, entityRevision };
  }

  private queueEntityRefresh(entityRevision: number): Promise<void> {
    const refresh = this.entityRefreshQueue
      .catch(() => undefined)
      .then(() => this.refreshEntities(entityRevision));
    this.entityRefreshQueue = refresh;
    return refresh;
  }

  private rebuildTerrainChunks(): void {
    const factory = this.terrainFactory;
    if (factory === undefined) return;
    for (const [key, previous] of [...this.terrainChunks]) {
      const [xText, zText] = key.split(':');
      const replacement = factory.create({ x: Number(xText), z: Number(zText) });
      this.world.remove(previous);
      previous.geometry.dispose();
      this.world.add(replacement);
      this.terrainChunks.set(key, replacement);
    }
  }

  private legacyVisibleChunkCoordinates(
    source: SourceSnapshot,
  ): readonly Readonly<{ x: number; z: number }>[] {
    const centerTileX =
      source.camera.mode === 'orbit' ? source.camera.centerTileX : source.camera.markerTileX;
    const centerTileZ =
      source.camera.mode === 'orbit' ? source.camera.centerTileZ : source.camera.markerTileZ;
    const centerChunkX = Math.floor((centerTileX + source.terrain.sampleWidth - 1) / 32);
    const centerChunkZ = Math.floor((centerTileZ + source.terrain.sampleWidth - 1) / 32);
    const coordinates: Readonly<{ x: number; z: number }>[] = [];
    for (let dz = -1; dz <= 1; dz += 1)
      for (let dx = -1; dx <= 1; dx += 1) {
        const chunkX = centerChunkX + dx;
        const chunkZ = centerChunkZ + dz;
        if (
          chunkX >= 0 &&
          chunkX < (source.terrain.sampleWidth - 1) / 16 &&
          chunkZ >= 0 &&
          chunkZ < (source.terrain.sampleWidth - 1) / 16
        )
          coordinates.push({ x: chunkX, z: chunkZ });
      }
    return coordinates;
  }

  private replaceActors(actors: readonly PublicActor[]): void {
    this.avatarScene?.replace(
      actors.map((actor) => publicActorPresence(actor, this.publicId(actor.actorId))),
    );
  }

  private replaceCharacters(characters: readonly PublicCharacter[]): void {
    const payload: VillageCharactersPayload = {
      streamGeneration: this.status?.generation ?? 1,
      villageId: 0,
      revision: this.source?.entityRevision ?? 0,
      characters: characters.map((character) => ({
        characterId: this.publicId(character.actorId),
        posX: character.posX,
        posZ: character.posZ,
        rotation: character.rotation ?? 16_384,
        data: '',
        familyId: 0,
        homeId: 0,
        inBuildingId: character.inBuilding ? 1 : 0,
        firstName: '',
        surname: '',
        type: character.type,
        consumed: false,
        pet: character.petType > 0 || character.petNetworkId !== 0,
        petType: character.petType,
        petNetworkId: character.petNetworkId,
        petName: '',
        petLevel: 0,
        petPointsWithinLevel: 0,
        mountType: character.mountType,
        mountName: '',
        noboType: character.noboType,
      })),
    };
    this.avatarScene?.replaceCharacters(payload);
  }

  private publicId(alias: string): number {
    const retained = this.publicIds.get(alias);
    if (retained !== undefined) return retained;
    const id = this.nextPublicId;
    this.nextPublicId += 1;
    this.publicIds.set(alias, id);
    return id;
  }

  private async poll(): Promise<void> {
    this.polling = true;
    const signal = this.abort.signal;
    let restart = false;
    try {
      while (!this.disposed && !this.inactive && !document.hidden && this.source !== undefined) {
        const source = this.source;
        const response = await fetch(
          `/api/v2/public-camera/sources/${encodeURIComponent(source.sourceId)}/notifications?afterGeneration=${String(this.status?.generation ?? 0)}&afterEntityRevision=${String(source.entityRevision)}&waitMilliseconds=10000`,
          { headers: this.viewerHeaders(), signal },
        );
        if (response.status === 404) {
          await this.refresh(true);
          restart = typeof this.status?.source === 'string';
          return;
        }
        if (!response.ok) throw new Error('Camera notifications unavailable');
        const payload = (await response.json()) as {
          notifications: Readonly<{
            sourceChanged?: boolean;
            generation: number;
            actors?: readonly PublicActor[];
            characters?: readonly PublicCharacter[];
            gateStates?: readonly PublicGateState[];
            productionStates?: readonly PublicProductionState[];
            status?: CameraStatus;
            entityRevision: number;
            serverTime: number;
            topics: readonly string[];
          }>;
        };
        if (payload.notifications.sourceChanged) {
          await this.refresh(true);
          restart = true;
          return;
        }
        if (payload.notifications.status) this.status = payload.notifications.status;
        this.serverOffset = payload.notifications.serverTime - Date.now();
        if (payload.notifications.characters)
          this.replaceCharacters(payload.notifications.characters);
        if (payload.notifications.actors) this.replaceActors(payload.notifications.actors);
        const production =
          payload.notifications.productionStates === undefined
            ? { changed: false, becameReady: [] as number[] }
            : this.replaceProductionStates(payload.notifications.productionStates, true);
        if (payload.notifications.topics.includes('world') || production.changed)
          await this.queueEntityRefresh(payload.notifications.entityRevision);
        for (const buildingId of production.becameReady) this.readyEffects.trigger(buildingId);
        if (payload.notifications.gateStates)
          this.replaceGateStates(payload.notifications.gateStates);
      }
    } catch {
      if (!signal.aborted) this.fallback();
    } finally {
      this.polling = false;
      if (
        (restart || (signal.aborted && signal !== this.abort.signal)) &&
        !this.disposed &&
        !this.inactive &&
        !document.hidden &&
        this.source !== undefined
      )
        void this.poll();
    }
  }

  private readonly renderFrame = (timestamp: number): void => {
    if (this.disposed || this.inactive || document.hidden || this.renderer === undefined) return;
    this.frame = requestAnimationFrame(this.renderFrame);
    if (timestamp - this.lastFrame < FRAME_MILLISECONDS) return;
    const deltaSeconds = Math.min(0.1, Math.max(0, (timestamp - this.lastFrame) / 1000));
    this.lastFrame = timestamp;
    this.resize();
    this.updateCamera();
    if (this.terrain?.villageId === CREATIVE_VILLAGE_ID) {
      const environment = this.creativeEnvironment.update(
        this.camera.position.x,
        this.camera.position.z,
        deltaSeconds,
        this.camera.position.y,
      );
      this.ambient?.color.setRGB(...environment.ambientColor);
      this.creativeTerrainAmbient.setRGB(...environment.ambientColor);
      if (this.scene.fog instanceof THREE.Fog) {
        this.scene.fog.color.setRGB(...environment.fogColor);
        this.scene.fog.near = environment.fogNear;
        this.scene.fog.far = environment.fogFar;
      }
      this.scene.background =
        this.camera.position.y < 0
          ? this.underwaterBackground.setRGB(...environment.fogColor)
          : (this.skybox?.texture ?? null);
    }
    this.waterSurface?.update((Date.now() + this.serverOffset) / 1000, this.camera);
    this.worldAssets?.update(FRAME_MILLISECONDS / 1000, Date.now() + this.serverOffset);
    this.readyEffects.update(deltaSeconds);
    this.avatarScene?.update(deltaSeconds);
    if (this.terrain?.villageId === CREATIVE_VILLAGE_ID) {
      this.waterVisibility.render(this.renderer, this.scene, this.camera, this.water, () =>
        this.renderer?.render(this.scene, this.camera),
      );
    } else this.renderer.render(this.scene, this.camera);
  };

  private updateCamera(): void {
    const source = this.source;
    if (source === undefined) return;
    const config = source.camera;
    const serverNow = Date.now() + this.serverOffset;
    const markerGroundY = this.terrainHeightAtTile(config.markerTileX, config.markerTileZ);
    let targetX: number,
      targetY: number,
      targetZ: number,
      desiredX: number,
      desiredZ: number,
      desiredY: number,
      focusGroundY: number;
    if (config.mode === 'fixed') {
      focusGroundY = markerGroundY;
      targetX = desiredX = (config.markerTileX + 0.5) * TILE_WORLD_SCALE;
      targetZ = desiredZ = (config.markerTileZ + 0.5) * TILE_WORLD_SCALE;
      desiredY = markerGroundY + config.fixedHoverHeight;
      targetY = desiredY;
      const angle = config.rotation * Math.PI * 0.5;
      targetX += Math.sin(angle) * 10;
      targetZ += Math.cos(angle) * 10;
    } else {
      targetX = config.centerTileX * TILE_WORLD_SCALE;
      targetZ = config.centerTileZ * TILE_WORLD_SCALE;
      const centerGroundY = this.terrainHeightAtTile(config.centerTileX, config.centerTileZ);
      focusGroundY = centerGroundY;
      targetY = centerGroundY + 0.7;
      const angle = cameraDroneOrbitAngle(serverNow, config.revolutionSeconds, this.reducedMotion);
      desiredX = targetX + config.radius * TILE_WORLD_SCALE * Math.cos(angle);
      desiredZ = targetZ + config.radius * TILE_WORLD_SCALE * Math.sin(angle);
      desiredY = centerGroundY + config.height;
    }
    const elapsed = Math.max(0, serverNow - (this.status?.liveSince ?? serverNow));
    const takeoff = cameraDroneTransitionProgress(elapsed, 3000, this.reducedMotion);
    const markerX = (config.markerTileX + 0.5) * TILE_WORLD_SCALE;
    const markerZ = (config.markerTileZ + 0.5) * TILE_WORLD_SCALE;
    this.camera.position.set(
      THREE.MathUtils.lerp(markerX, desiredX, takeoff),
      THREE.MathUtils.lerp(markerGroundY + 0.25, desiredY, takeoff),
      THREE.MathUtils.lerp(markerZ, desiredZ, takeoff),
    );
    this.camera.lookAt(targetX, targetY, targetZ);
    if (this.sun !== undefined)
      updateLegacyWorldShadowProjection(this.sun, this.camera, focusGroundY);
  }

  private terrainHeightAtTile(tileX: number, tileZ: number): number {
    const terrain = this.terrain;
    if (terrain === undefined) return 0;
    const worldX = tileX * TILE_WORLD_SCALE;
    const worldZ = tileZ * TILE_WORLD_SCALE;
    return terrain.sampleHeight(terrain.worldToSample(worldX), terrain.worldToSample(worldZ));
  }

  private objectLevitationAt(worldX: number, worldZ: number): number {
    const worldAssets = this.worldAssets;
    if (worldAssets === undefined) return 0;
    const gridX = Math.floor(worldX * 2 + 128);
    const gridZ = Math.floor(worldZ * 2 + 128);
    const cacheKey = gridZ * 256 + gridX;
    if (worldAssets.buildingLevitationMetadataReady()) {
      const cached = this.objectLevitationByGridCell.get(cacheKey);
      if (cached !== undefined) return cached;
    }
    const levitation = legacyObjectLevitationAt(
      this.entityPayloads.values(),
      worldX,
      worldZ,
      (buildingType, rotation) => worldAssets.buildingLevitationSurface(buildingType, rotation),
    );
    if (worldAssets.buildingLevitationMetadataReady()) {
      this.objectLevitationByGridCell.set(cacheKey, levitation);
    }
    return levitation;
  }

  private replaceGateStates(states: readonly PublicGateState[]): void {
    const next = new Map(states.map((state) => [state.buildingId, state.open]));
    for (const [buildingId, open] of next) {
      const previous = this.gateStates.get(buildingId);
      if (previous === open || (previous === undefined && !open)) continue;
      this.worldAssets?.triggerBuildingOpenClose(buildingId, open);
      this.worldAssets?.triggerBuildingKeyActivator(buildingId, open);
      this.worldAssets?.triggerBuildingKeyTarget(buildingId, open);
      this.worldAssets?.triggerBuildingSelfUnlock(buildingId, open);
    }
    this.gateStates.clear();
    for (const [buildingId, open] of next) this.gateStates.set(buildingId, open);
  }

  private restoreOpenGateStates(): void {
    for (const [buildingId, open] of this.gateStates) {
      if (!open) continue;
      this.worldAssets?.triggerBuildingOpenClose(buildingId, true);
      this.worldAssets?.triggerBuildingKeyActivator(buildingId, true);
      this.worldAssets?.triggerBuildingKeyTarget(buildingId, true);
      this.worldAssets?.triggerBuildingSelfUnlock(buildingId, true);
    }
  }

  private replaceProductionStates(
    states: readonly PublicProductionState[],
    animate: boolean,
  ): Readonly<{ changed: boolean; becameReady: readonly number[] }> {
    let changed = false;
    const becameReady: number[] = [];
    const next = new Map(states.map((state) => [state.buildingId, state]));
    for (const [buildingId, previous] of this.productionStates) {
      if (!next.has(buildingId) && previous.ready) changed = true;
    }
    for (const [buildingId, state] of next) {
      const previous = this.productionStates.get(buildingId);
      if (previous?.ready !== state.ready) {
        changed = changed || previous !== undefined;
        if (animate && state.ready && previous?.ready === false) becameReady.push(buildingId);
      }
    }
    this.productionStates.clear();
    for (const [buildingId, state] of next) this.productionStates.set(buildingId, state);
    this.scheduleProductionReadyTransition();
    return { changed, becameReady };
  }

  private scheduleProductionReadyTransition(): void {
    clearTimeout(this.productionReadyTimeout);
    this.productionReadyTimeout = undefined;
    const serverNow = Date.now() + this.serverOffset;
    let nextReadyAt = Number.POSITIVE_INFINITY;
    for (const state of this.productionStates.values()) {
      if (!state.ready && state.readyAt > 0) nextReadyAt = Math.min(nextReadyAt, state.readyAt);
    }
    if (!Number.isFinite(nextReadyAt)) return;
    this.productionReadyTimeout = setTimeout(
      () => void this.applyDueProductionTransitions(),
      Math.max(0, nextReadyAt - serverNow + 50),
    );
  }

  private async applyDueProductionTransitions(): Promise<void> {
    this.productionReadyTimeout = undefined;
    if (this.disposed || this.inactive || document.hidden || this.source === undefined) return;
    const serverNow = Date.now() + this.serverOffset;
    const becameReady: number[] = [];
    for (const [buildingId, state] of this.productionStates) {
      if (state.ready || state.readyAt <= 0 || state.readyAt > serverNow) continue;
      this.productionStates.set(buildingId, { ...state, ready: true });
      becameReady.push(buildingId);
    }
    this.scheduleProductionReadyTransition();
    if (becameReady.length === 0) return;
    const sourceId = this.source.sourceId;
    await this.queueEntityRefresh(this.source.entityRevision);
    if (this.currentSourceId() !== sourceId) return;
    for (const buildingId of becameReady) this.readyEffects.trigger(buildingId);
  }

  private currentSourceId(): string | undefined {
    return this.source?.sourceId;
  }

  private resize(): void {
    if (this.renderer === undefined) return;
    const width = Math.max(1, this.canvas.clientWidth);
    const height = Math.max(1, this.canvas.clientHeight);
    if (width === this.lastCanvasWidth && height === this.lastCanvasHeight) return;
    this.lastCanvasWidth = width;
    this.lastCanvasHeight = height;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  private disposeWorld(): void {
    this.avatarScene?.dispose();
    this.avatarScene = undefined;
    this.publicIds.clear();
    this.nextPublicId = 1;
    for (const group of this.entityGroups.values()) {
      this.world.remove(group);
      this.worldAssets?.release(group);
    }
    this.entityGroups.clear();
    this.entityPayloads.clear();
    this.objectLevitationByGridCell.clear();
    this.terrainChunks.clear();
    this.terrainFactory = undefined;
    this.gateStates.clear();
    this.productionStates.clear();
    clearTimeout(this.productionReadyTimeout);
    this.productionReadyTimeout = undefined;
    this.readyEffects.clear();
    this.terrain = undefined;
    this.sun = undefined;
    this.terrainTexture?.dispose();
    this.terrainTexture = undefined;
    this.waterSurface = undefined;
    this.water = undefined;
    this.waterTexture?.dispose();
    this.waterTexture = undefined;
    this.skybox?.dispose();
    this.skybox = undefined;
    this.scene.background = new THREE.Color(0x91c6df);
    this.scene.fog = new THREE.Fog(0x91c6df, 45, 125);
    for (const child of [...this.world.children]) {
      this.world.remove(child);
      child.traverse(disposeObject);
    }
  }

  private fallback(): void {
    this.canvas.classList.remove('public-camera--ready');
    cancelAnimationFrame(this.frame);
  }

  private viewerHeaders(): HeadersInit {
    return this.viewerToken === undefined
      ? {}
      : { [PUBLIC_CAMERA_VIEWER_HEADER]: this.viewerToken };
  }

  private resetInactivityTimeout(): void {
    if (this.disposed || this.inactive) return;
    clearTimeout(this.inactivityTimeout);
    this.inactivityTimeout = setTimeout(
      () => this.stopForInactivity(),
      PUBLIC_CAMERA_INACTIVITY_MILLISECONDS,
    );
  }

  private stopForInactivity(): void {
    if (this.disposed || this.inactive) return;
    this.inactive = true;
    this.abort.abort();
    clearTimeout(this.statusRetry);
    this.fallback();
    this.releaseViewer();
  }

  private releaseViewer(): void {
    const token = this.viewerToken;
    this.viewerToken = undefined;
    if (token === undefined) return;
    void fetch('/api/v2/public-camera/viewer', {
      method: 'DELETE',
      headers: { [PUBLIC_CAMERA_VIEWER_HEADER]: token },
      keepalive: true,
    }).catch(() => undefined);
  }
}

function publicEntityChunk(source: EntityChunk, width = 129): EntityChunkPayload {
  const minimumTileX = source.chunkX * 32 - (width - 1);
  const minimumTileZ = source.chunkZ * 32 - (width - 1);
  const buildings = source.buildings
    .filter((building) => building.type !== 6200 && building.type !== 6201)
    .map((building) => ({
      id: building.id,
      type: building.type,
      remainingPoints: building.ready ? 0 : 1,
      ...(building.placementY == null
        ? {}
        : { placementY: building.placementY, flattenTerrain: false }),
      placementOffsetX: building.placementOffsetX ?? 0,
      placementOffsetZ: building.placementOffsetZ ?? 0,
      placementTiltX: building.placementTiltX ?? 0,
      placementTiltZ: building.placementTiltZ ?? 0,
      tileX: building.tileX,
      tileZ: building.tileZ,
      parameters: '',
      entityText: '',
      interaction: null,
      open: building.open,
      pictureId: building.pictureId,
      gift: null,
      rotation: building.rotation,
      consumed: false,
      placedOnMap: true,
      ready: building.ready,
      delayStart: 0,
      delayEnd: 0,
      ingredients: 0,
      sellRefund: 0,
      readySellRefund: 0,
    }));
  return {
    villageId: width > 129 ? CREATIVE_VILLAGE_ID : 0,
    ...(source.terrainChunk === undefined ? {} : { terrainChunk: source.terrainChunk }),
    ...(source.creativeSpatial === undefined ? {} : { creativeSpatial: source.creativeSpatial }),
    revision: source.revision,
    chunkX: source.chunkX,
    chunkZ: source.chunkZ,
    minimumTileX,
    minimumTileZ,
    maximumTileXExclusive: minimumTileX + 32,
    maximumTileZExclusive: minimumTileZ + 32,
    snapshotBuildingCount: buildings.length,
    snapshotTreeCount: source.trees.length,
    buildings,
    trees: source.trees,
  };
}

function chunkKey(x: number, z: number): string {
  return `${String(x)}:${String(z)}`;
}

export function publicActorPresence(actor: PublicActor, playerId: number): BrowserAvatarPresence {
  const juggling =
    typeof actor.juggling === 'object' && actor.juggling !== null
      ? (actor.juggling satisfies AvatarJugglingPayload)
      : null;
  return {
    playerId,
    name: '',
    friend: false,
    mountType: actor.mountType,
    mountCharacterId: 0,
    petType: actor.petType,
    petNetworkId: actor.petNetworkId,
    noboType: actor.noboType ?? 0,
    avatarData: '',
    gender: actor.gender,
    appearanceLayers: actor.appearanceLayers,
    mountName: '',
    petName: '',
    petLevel: 0,
    petPointsWithinLevel: 0,
    petCoolIdleUnlockedUntil: 0,
    level: 0,
    posX: actor.posX,
    posY: actor.posY ?? 0,
    posZ: actor.posZ,
    rotation: actor.rotation,
    positionKnown: true,
    positionSource: 'server-notification',
    pendingMovementSequence: 0,
    away: actor.away,
    emote: actor.emote as AvatarEmoteAction | null,
    sit: actor.sit ? { buildingId: 0, action: 'sit' } : null,
    juggling,
    worldX: actor.posX / 500,
    worldY: (actor.posY ?? 0) / 500,
    worldZ: actor.posZ / 500,
  };
}

function disposeObject(object: THREE.Object3D): void {
  if (!(object instanceof THREE.Mesh)) return;
  const mesh = object as THREE.Mesh;
  mesh.geometry.dispose();
  for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material])
    material.dispose();
}
